/**
 * API client — thin wrapper over fetch.
 * All endpoints are behind /api/* on the same origin (proxied via CloudFront).
 * Admin routes use the Supabase access token from the current session.
 */
import { supabase } from "./supabase";

const BASE = "/api";

async function authHeaders(): Promise<HeadersInit> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `${res.status} ${res.statusText}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

async function authRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const auth = await authHeaders();
  return request<T>(path, {
    ...init,
    headers: { ...auth, ...(init.headers || {}) },
  });
}

// ── Types ────────────────────────────────────────────────────────────
export type LinkMode = "public" | "nominative";

export interface LinkPublic {
  link_id: string;
  text: string;
  max_duration_sec: number;
  mode: LinkMode;
  recipient_name?: string;
  briefing_video_url?: string;
}

export interface LinkAdmin {
  link_id: string;
  title: string;
  text: string;
  max_duration_sec: number;
  mode: LinkMode;
  recipient_name?: string;
  recipient_email?: string;
  token?: string;
  created_at: string;
  expires_at?: string;
  url: string;
  has_briefing: boolean;
  video_count: number;
}

export interface CreateLinkRequest {
  text: string;
  mode: LinkMode;
  title?: string;
  recipient_name?: string;
  recipient_email?: string;
  max_duration_sec: number;
  expires_in_days?: number;
  briefing_video_s3_key?: string;
}

export interface BriefingSummary {
  s3_key: string;
  last_used_at: string;
  preview_url: string;
}

export interface InitUploadRequest {
  link_id: string;
  token?: string;
  mime_type: string;
  recorder_name?: string;
  recorder_email?: string;
}

export interface InitUploadResponse {
  video_id: string;
  upload_url: string;
  s3_key: string;
}

export interface InitBriefingUploadRequest {
  mime_type: string;
}

export interface InitBriefingUploadResponse {
  briefing_id: string;
  upload_url: string;
  s3_key: string;
}

export interface Video {
  video_id: string;
  link_id: string;
  link_title?: string;
  s3_key: string;
  status: string;
  mime_type: string;
  size_bytes?: number;
  duration_sec?: number;
  recorder_name?: string;
  recorder_email?: string;
  uploaded_at: string;
}

export interface FormatInfo {
  ready: boolean;
  download_url?: string;
  size_bytes?: number;
}

export interface VideoDetail {
  video_id: string;
  link_id: string;
  link_title?: string;
  uploaded_at: string;
  preview_url: string;
  filename_base: string;
  webm: FormatInfo;
  mp4: FormatInfo;
  mov: FormatInfo;
  convert_error?: string;
}

// ── Public ───────────────────────────────────────────────────────────
export function getLink(linkId: string, token?: string): Promise<LinkPublic> {
  const qs = token ? `?token=${encodeURIComponent(token)}` : "";
  return request<LinkPublic>(`/links/${linkId}${qs}`);
}

export function initVideoUpload(
  req: InitUploadRequest
): Promise<InitUploadResponse> {
  return request<InitUploadResponse>(`/videos/init`, {
    method: "POST",
    body: JSON.stringify(req),
  });
}

// ── Admin (requires Supabase session) ────────────────────────────────
// Login / signup / logout / password reset are handled by `supabase.auth.*`
// directly in the components — no need for dedicated API calls here.

export function listVideos(titleFilter?: string): Promise<Video[]> {
  const qs = titleFilter
    ? `?title=${encodeURIComponent(titleFilter)}`
    : "";
  return authRequest<Video[]>(`/admin/videos${qs}`);
}

export function getVideoDetail(videoId: string): Promise<VideoDetail> {
  return authRequest<VideoDetail>(`/admin/videos/${videoId}/detail`);
}

export function listLinks(): Promise<LinkAdmin[]> {
  return authRequest<LinkAdmin[]>(`/admin/links`);
}

export function createLink(req: CreateLinkRequest): Promise<LinkAdmin> {
  return authRequest<LinkAdmin>(`/admin/links`, {
    method: "POST",
    body: JSON.stringify(req),
  });
}

export function initBriefingUpload(
  mimeType: string
): Promise<InitBriefingUploadResponse> {
  return authRequest<InitBriefingUploadResponse>(`/admin/briefing-videos/init`, {
    method: "POST",
    body: JSON.stringify({ mime_type: mimeType }),
  });
}

export function listBriefings(): Promise<BriefingSummary[]> {
  return authRequest<BriefingSummary[]>(`/admin/briefings`);
}

// ── Helper: upload a file to a pre-signed PUT URL ────────────────────
export async function uploadToPresignedUrl(
  url: string,
  file: Blob,
  contentType: string,
  onProgress?: (pct: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", contentType);
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
    };
    xhr.onerror = () => reject(new Error("Upload network error"));
    xhr.send(file);
  });
}
